// Best-effort repair of LLM-authored mermaid that fails to parse because labels
// carry unquoted special characters. Mermaid's prescribed fix is to wrap node
// label text in quotes, and to put edge label text in the piped form. This is a
// pragmatic heuristic, NOT a full mermaid parser.
//
// SAFETY: autoFixMermaid() is only ever run on a diagram that ALREADY failed to
// render (see renderMermaidFallback's onRenderError path), and the caller
// re-parses the result before showing it. So a wrong guess can never break a
// diagram that was rendering fine - worst case the repaired text still doesn't
// parse and we keep showing the raw source. That net is what lets the heuristic
// stay simple.

// Characters that break an UNQUOTED node label: `&` (the node-join operator),
// `(` `)` (round-shape delimiters), `<` `>` (asymmetric / inline html). `/`, `*`
// etc. are fine inside brackets, so we leave labels that only contain those.
const NODE_BAD = /[&()<>]/;

// Node-shape delimiter pairs, opens ordered longest-first: a left-to-right scan
// must prefer `[[` over `[`, `[(` over `[`, etc.
const PAIR: Record<string, string> = {
  '[[': ']]', '[(': ')]', '([': '])', '((': '))', '{{': '}}',
  '[': ']', '(': ')', '{': '}',
};
const OPENS = ['[[', '[(', '([', '((', '{{', '[', '(', '{'];

function isWord(ch: string): boolean {
  return /[\w-]/.test(ch);
}

function isQuoted(t: string): boolean {
  const s = t.trim();
  return s.length >= 2 && s.startsWith('"') && s.endsWith('"');
}

function maybeQuote(inner: string): string {
  if (!NODE_BAD.test(inner) || isQuoted(inner)) return inner;
  // `#quot;` is mermaid's entity for a literal double-quote inside a label.
  return `"${inner.replace(/"/g, '#quot;')}"`;
}

function openAt(src: string, pos: number): string | null {
  for (const o of OPENS) if (src.startsWith(o, pos)) return o;
  return null;
}

// Walk the source. At each `id<shape-open>` we find the shape's matching close,
// quote the label between them if needed, then skip PAST the whole shape - so
// parens that live inside a label (`[a (b)]`) are never re-scanned as their own
// node, which is what a single regex with delimiter alternation gets wrong.
function fixNodeLabels(src: string): string {
  let out = '';
  let i = 0;
  const n = src.length;
  while (i < n) {
    const ch = src[i] ?? '';
    if (!isWord(ch)) {
      out += ch;
      i += 1;
      continue;
    }
    let j = i;
    while (j < n && isWord(src[j] ?? '')) j += 1;
    const id = src.slice(i, j);
    const open = openAt(src, j);
    const close = open ? PAIR[open] : undefined;
    if (open && close) {
      const start = j + open.length;
      const end = src.indexOf(close, start);
      const inner = end === -1 ? '' : src.slice(start, end);
      // A label never spans lines; if it appears to, we mis-detected - skip.
      if (end !== -1 && !inner.includes('\n')) {
        out += id + open + maybeQuote(inner) + close;
        i = end + close.length;
        continue;
      }
    }
    out += id;
    i = j;
  }
  return out;
}

// Solid edge with a text label: `A -- text --> B`. The piped form reliably
// accepts a quoted label, so we rewrite to `A -->|"text"| B`. Only this link
// style is handled; anything else falls through to the source fallback.
const EDGE_RE = /--\s+([^\n>]+?)\s+-->/g;
const EDGE_BAD = /[^\w\s]/;

function fixEdgeLabels(src: string): string {
  return src.replace(EDGE_RE, (whole, label: string) => {
    if (!EDGE_BAD.test(label) || isQuoted(label)) return whole;
    return `-->|"${label.replace(/"/g, '#quot;')}"|`;
  });
}

/** Return a repaired copy of the diagram source (or the input unchanged if the
 *  heuristic finds nothing to quote). */
export function autoFixMermaid(src: string): string {
  return fixEdgeLabels(fixNodeLabels(src));
}
